import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical } from 'lucide-react';

interface SortablePointItemProps {
  id: string;
  index: number;
  text: string;
  canRemove: boolean;
  pendingRemove: boolean;
  disabled: boolean;
  onTextChange: (index: number, text: string) => void;
  onRemoveClick: (index: number) => void;
  onRemoveConfirm: (index: number) => void;
  onRemoveCancel: () => void;
  textareaRef: (el: HTMLTextAreaElement | null) => void;
}

export function SortablePointItem({
  id,
  index,
  text,
  canRemove,
  pendingRemove,
  disabled,
  onTextChange,
  onRemoveClick,
  onRemoveConfirm,
  onRemoveCancel,
  textareaRef,
}: SortablePointItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, disabled });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="border-[1.5px] border-pine-line hover:border-pine-pine rounded-[8px] p-[8px_10px] bg-white mb-[6px] group relative transition-colors"
    >
      <div className="flex items-start gap-1.5">
        <button
          type="button"
          data-testid={`point-drag-handle-${index}`}
          {...listeners}
          {...attributes}
          className="cursor-grab active:cursor-grabbing mt-[2px] shrink-0 touch-none text-pine-ink-soft hover:text-pine-pine transition-colors"
          aria-label="Drag to reorder"
        >
          <GripVertical className="w-3.5 h-3.5" />
        </button>
        <span className="text-[11px] text-pine-ink-soft font-semibold mt-[2px] shrink-0">
          {index + 1}.
        </span>
        <textarea
          ref={textareaRef}
          data-testid={`point-input-${index}`}
          value={text}
          onChange={(e) => onTextChange(index, e.target.value)}
          rows={2}
          placeholder="Describe this step..."
          className="text-[12px] leading-[1.4] w-full bg-transparent outline-none resize-none"
        />
      </div>
      {pendingRemove ? (
        <div className="flex items-center justify-end gap-2 mt-1.5 pt-1.5 border-t border-pine-line">
          <span className="text-[11px] text-pine-ink-soft">Remove this point?</span>
          <button
            type="button"
            onClick={onRemoveCancel}
            className="text-[11px] font-semibold text-pine-ink-soft hover:text-pine-ink transition-colors"
          >
            Cancel
          </button>
          <button
            data-testid={`point-remove-confirm-${index}`}
            type="button"
            onClick={() => onRemoveConfirm(index)}
            className="text-[11px] font-semibold text-pine-danger hover:underline"
          >
            Remove
          </button>
        </div>
      ) : canRemove ? (
        <button
          data-testid={`point-remove-${index}`}
          type="button"
          onClick={() => onRemoveClick(index)}
          className="absolute bottom-1.5 right-2 text-[13px] text-pine-ink-soft hover:text-pine-danger opacity-0 group-hover:opacity-100 transition-opacity"
          title="Remove point"
        >
          ×
        </button>
      ) : null}
    </div>
  );
}
